import { supabase } from '@/lib/supabase';
import { palavraDoGrupo } from './utils';
import type { EventoMeu, LinhaVagas } from './types';

// Números dos cartões de /meus-eventos. Só leitura: quando a fonte não responde,
// o cartão fica sem contador em vez de mostrar um número errado.

type ModalidadeLinha = {
  id: number;
  evento_id: number;
  nome: string | null;
  papel: string | null;
};

/** Evento de fichas: pedidos pagos na loja (mesmo número de "Compradores de fichas"). */
export async function preencherCompradoresFichas(lista: EventoMeu[]): Promise<void> {
  const alvos = lista.filter(
    (e) =>
      !e.so_divulgacao &&
      (e.entrada_franca === true || (!e.vende_ingressos && !e.usa_equipes)),
  );
  if (alvos.length === 0) return;

  const respostas = await Promise.allSettled(
    alvos.map((e) => supabase.rpc('loja_painel', { p_evento: e.id })),
  );

  respostas.forEach((r, i) => {
    if (r.status !== 'fulfilled' || r.value.error || !r.value.data) return;
    const painel = r.value.data as { pedidos_pagos?: number | null } | null;
    const pagos = painel?.pedidos_pagos;
    if (pagos === null || pagos === undefined) return;
    alvos[i].compradoresFichas = Number(pagos) || 0;
  });
}

/**
 * Acampamento: com vaga (pendente/confirmada/isenta) e suplentes (lista de espera)
 * separados. Equipe nunca soma com campista — a mesma leitura dos cartões do painel.
 */
export async function preencherVagasAcampamento(lista: EventoMeu[]): Promise<void> {
  const alvos = lista.filter(
    (e) => !e.so_divulgacao && e.entrada_franca !== true && !e.vende_ingressos && e.usa_equipes,
  );
  if (alvos.length === 0) return;

  const ids = alvos.map((e) => e.id);

  try {
    const [resMods, respostas] = await Promise.all([
      supabase.from('modalidades').select('id, evento_id, nome, papel').in('evento_id', ids),
      Promise.allSettled(ids.map((id) => supabase.rpc('painel_vagas', { p_evento: id }))),
    ]);
    if (resMods.error) return;

    const papelPorModalidade = new Map<number, string>();
    const nomesPorEvento = new Map<number, string[]>();
    ((resMods.data as ModalidadeLinha[] | null) || []).forEach((m) => {
      if (m.papel) papelPorModalidade.set(Number(m.id), m.papel);
      const nomes = nomesPorEvento.get(Number(m.evento_id)) ?? [];
      if (m.nome) nomes.push(m.nome);
      nomesPorEvento.set(Number(m.evento_id), nomes);
    });

    respostas.forEach((r, i) => {
      if (r.status !== 'fulfilled' || r.value.error || !r.value.data) return;
      const grupos = r.value.data as LinhaVagas[];
      if (!Array.isArray(grupos) || grupos.length === 0) return;

      let totalComVaga = 0;
      let totalSuplentes = 0;
      let equipeComVaga = 0;
      let campistasComVaga = 0;

      grupos.forEach((g) => {
        const comVaga = Number(g.com_vaga) || 0;
        totalComVaga += comVaga;
        totalSuplentes += Number(g.suplentes) || 0;
        const papel = papelPorModalidade.get(Number(g.modalidade_id));
        if (papel === 'equipe') equipeComVaga += comVaga;
        if (papel === 'campista') campistasComVaga += comVaga;
      });

      alvos[i].vagas = {
        totalComVaga,
        totalSuplentes,
        equipeComVaga,
        campistasComVaga,
        palavra: palavraDoGrupo(nomesPorEvento.get(alvos[i].id) ?? []),
      };
    });
  } catch {
    // Sem esses números o cartão só não mostra contador.
  }
}